<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import VenueSelect from './VenueSelect.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import type { StoreEditionPayload, UpdateEditionPayload } from '@/types/events'

interface EditionFormData {
  name: string
  start_date: string
  end_date: string
  venue_id: number | null
  active: boolean
}

const props = defineProps<{
  modelValue: EditionFormData
  loading: boolean
  errors: Record<string, string[]>
  organisationId: number
  isEdit?: boolean
  submitLabel?: string
}>()

const emit = defineEmits<{
  'update:modelValue': [value: EditionFormData]
  submit: [data: StoreEditionPayload | UpdateEditionPayload]
  cancel: []
}>()

const { t } = useI18n()

function update<K extends keyof EditionFormData>(field: K, value: EditionFormData[K]): void {
  emit('update:modelValue', { ...props.modelValue, [field]: value })
}

function handleSubmit(): void {
  if (props.isEdit) {
    const payload: UpdateEditionPayload = {
      name: props.modelValue.name,
      start_date: props.modelValue.start_date,
      end_date: props.modelValue.end_date,
      venue_id: props.modelValue.venue_id,
    }
    emit('submit', payload)
  } else {
    const payload: StoreEditionPayload = {
      name: props.modelValue.name,
      start_date: props.modelValue.start_date,
      end_date: props.modelValue.end_date,
      venue_id: props.modelValue.venue_id,
      active: props.modelValue.active,
    }
    emit('submit', payload)
  }
}
</script>

<template>
  <form novalidate class="space-y-6" @submit.prevent="handleSubmit">
    <!-- Name -->
    <div>
      <label for="edition-name" class="block text-sm font-medium text-slate-300 mb-1">
        {{ t('editions.form.name_label') }}
        <span class="text-red-400 ml-0.5">*</span>
      </label>
      <input
        id="edition-name"
        :value="modelValue.name"
        type="text"
        :placeholder="t('editions.form.name_placeholder')"
        class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
        :class="errors['name'] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
        @input="update('name', ($event.target as HTMLInputElement).value)"
      />
      <p v-if="errors['name']" class="mt-1 text-xs text-red-400">{{ errors['name'][0] }}</p>
    </div>

    <!-- Date range -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label for="edition-start-date" class="block text-sm font-medium text-slate-300 mb-1">
          {{ t('editions.form.start_date_label') }}
          <span class="text-red-400 ml-0.5">*</span>
        </label>
        <input
          id="edition-start-date"
          :value="modelValue.start_date"
          type="date"
          class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
          :class="errors['start_date'] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
          @input="update('start_date', ($event.target as HTMLInputElement).value)"
        />
        <p v-if="errors['start_date']" class="mt-1 text-xs text-red-400">{{ errors['start_date'][0] }}</p>
      </div>
      <div>
        <label for="edition-end-date" class="block text-sm font-medium text-slate-300 mb-1">
          {{ t('editions.form.end_date_label') }}
          <span class="text-red-400 ml-0.5">*</span>
        </label>
        <input
          id="edition-end-date"
          :value="modelValue.end_date"
          type="date"
          class="w-full rounded-lg bg-slate-800 border px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
          :class="errors['end_date'] ? 'border-red-500' : 'border-slate-700 focus:border-emerald-500'"
          @input="update('end_date', ($event.target as HTMLInputElement).value)"
        />
        <p v-if="errors['end_date']" class="mt-1 text-xs text-red-400">{{ errors['end_date'][0] }}</p>
      </div>
    </div>

    <!-- Venue (required: races without their own venue inherit it) -->
    <div>
      <label class="block text-sm font-medium text-slate-300 mb-1">
        {{ t('editions.form.venue_label') }}
        <span class="text-red-400 ml-0.5">*</span>
      </label>
      <VenueSelect
        :model-value="modelValue.venue_id"
        :organisation-id="organisationId"
        :allow-empty="false"
        :errors="errors"
        @update:model-value="update('venue_id', $event)"
      />
      <p class="mt-1 text-xs text-slate-500">{{ t('editions.form.venue_required_hint') }}</p>
    </div>

    <!-- Active (only on create) -->
    <div v-if="!isEdit" class="flex items-center gap-2">
      <input
        id="edition-active"
        :checked="modelValue.active"
        type="checkbox"
        class="w-4 h-4 rounded accent-emerald-500 cursor-pointer"
        @change="update('active', ($event.target as HTMLInputElement).checked)"
      />
      <label for="edition-active" class="text-sm text-slate-300 cursor-pointer">
        {{ t('editions.form.active_label') }}
      </label>
    </div>

    <!-- Actions -->
    <div class="flex items-center gap-3 pt-2">
      <button
        type="submit"
        :disabled="loading || modelValue.venue_id === null"
        class="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors flex items-center gap-2"
      >
        <AppSpinner v-if="loading" size="button" />
        {{ loading ? t('common.loading') : (submitLabel ?? (isEdit ? t('editions.form.submit_update') : t('editions.form.submit_create'))) }}
      </button>
      <button
        type="button"
        class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
        @click="emit('cancel')"
      >
        {{ t('editions.form.cancel') }}
      </button>
    </div>
  </form>
</template>
